import { BACKEND_URL, getSessionId, authHeaders } from './utils';
import { getCountryCode } from './country';

const countryHeader = () => { const c = getCountryCode(); return c ? { 'X-Country': c } : {}; };

async function apiFetch(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const maxRetries = method === 'GET' ? 3 : 1;
  const timeoutMs = options.timeoutMs || 20000; // 20s default
  let lastErr;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(`${BACKEND_URL}${path}`, {
        headers: { 'Content-Type': 'application/json', ...countryHeader(), ...authHeaders(), ...options.headers },
        credentials: 'include',
        signal: ctrl.signal,
        ...options,
      });
      clearTimeout(timer);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err = new Error(body?.detail?.message || body?.detail || `API error ${res.status}`);
        err.status = res.status;
        err.code = body?.detail?.code;
        throw err;
      }
      return await res.json();
    } catch (err) {
      clearTimeout(timer);
      lastErr = err;
      const isAbort = err.name === 'AbortError';
      if (attempt < maxRetries - 1 && method === 'GET' && (isAbort || !err.status || err.status >= 500 || err.message === 'Failed to fetch')) {
        await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        continue;
      }
      throw err;
    }
  }
  throw lastErr;
}

const imagesCache = new Map();

export const api = {
  getCountries: () => apiFetch('/api/countries'),
  getHomepageData: () => apiFetch('/api/homepage-data'),
  getTrendingSearches: (limit = 10) => apiFetch(`/api/trending-searches?limit=${limit}`),
  searchAutocomplete: (q, limit = 8) => apiFetch(`/api/search-autocomplete?q=${encodeURIComponent(q)}&limit=${limit}`),
  getCategoryFacets: (q) => apiFetch(`/api/search/category-facets?q=${encodeURIComponent(q)}`),
  getRelatedSearches: (q, limit = 8) => apiFetch(`/api/related-searches?q=${encodeURIComponent(q)}&limit=${limit}`),
  getYearContext: (title) => apiFetch(`/api/year-context?title=${encodeURIComponent(title)}`),
  getSearchSuggestion: (q, zeroResults = false) => apiFetch(`/api/search-suggestion?q=${encodeURIComponent(q)}&zero_results=${zeroResults}`),
  inferCategory: (q) => apiFetch(`/api/search/infer-category?q=${encodeURIComponent(q)}`),
  getSubcategories: (canonical, limit = 8, lang = 'it') =>
    apiFetch(`/api/categories/subcategories?canonical=${encodeURIComponent(canonical)}&limit=${limit}&lang=${lang}`),
  search: (params) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.minPrice) qs.set('min_price', params.minPrice);
    if (params.maxPrice) qs.set('max_price', params.maxPrice);
    if (params.sources && params.sources.length) qs.set('sources', params.sources.join(','));
    if (params.category && params.category !== 'all') qs.set('category', params.category);
    if (params.canton && params.canton !== 'all') qs.set('canton', params.canton);
    if (params.year && params.year !== 'all') qs.set('year', params.year);
    if (params.sort) qs.set('sort', params.sort);
    if (params.page) qs.set('page', params.page);
    if (params.onlyFree) qs.set('only_free', 'true');
    if (params.categoryInferred) qs.set('category_inferred', 'true');
    if (params.nocat) qs.set('nocat', 'true');
    qs.set('page_size', params.pageSize || 20);
    return apiFetch(`/api/search?${qs.toString()}`);
  },

  getDeals: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.minDealScore) qs.set('min_deal_score', params.minDealScore);
    if (params.minPrice) qs.set('min_price', params.minPrice);
    if (params.maxPrice) qs.set('max_price', params.maxPrice);
    if (params.sources && params.sources.length) qs.set('sources', params.sources.join(','));
    if (params.category && params.category !== 'all') qs.set('category', params.category);
    if (params.canton && params.canton !== 'all') qs.set('canton', params.canton);
    if (params.sort && params.sort !== 'relevance') qs.set('sort', params.sort);
    if (params.page) qs.set('page', params.page);
    qs.set('page_size', params.pageSize || 20);
    return apiFetch(`/api/deals?${qs.toString()}`);
  },

  getNewest: (window = '1h', page = 1, sources = [], category = 'all', sort = 'newest', pageSize = 20, minPrice = null, maxPrice = null, onlyFree = false, canton = 'all', subcategory = '') => {
    const qs = new URLSearchParams();
    qs.set('window', window);
    qs.set('page', page);
    qs.set('page_size', pageSize);
    if (subcategory) qs.set('subcategory', subcategory);
    if (sources && sources.length) qs.set('sources', sources.join(','));
    if (category && category !== 'all') qs.set('category', category);
    if (canton && canton !== 'all') qs.set('canton', canton);
    if (sort && sort !== 'newest') qs.set('sort', sort);
    if (minPrice) qs.set('min_price', minPrice);
    if (maxPrice) qs.set('max_price', maxPrice);
    if (onlyFree) qs.set('only_free', 'true');
    return apiFetch(`/api/newest?${qs.toString()}`);
  },

  getStats: () => apiFetch('/api/stats'),

  getFallbackLinks: (q) => apiFetch(`/api/fallback-links?q=${encodeURIComponent(q || '')}`),

  saveSearch: (keyword, filters = {}) =>
    apiFetch('/api/saved-searches', {
      method: 'POST',
      body: JSON.stringify({
        keyword,
        session_id: getSessionId(),
        ...filters
      }),
    }),

  getSavedSearches: () =>
    apiFetch(`/api/saved-searches?session_id=${getSessionId()}`),

  deleteSavedSearch: (id) =>
    apiFetch(`/api/saved-searches/${id}`, { method: 'DELETE' }),

  saveListing: (listing) =>
    apiFetch('/api/saved-listings', {
      method: 'POST',
      body: JSON.stringify({
        session_id: getSessionId(),
        source: listing.source,
        source_listing_id: listing.source_listing_id,
        title: listing.title,
        price_chf: listing.price_chf,
        image_url: listing.image_url,
        listing_url: listing.listing_url,
        city: listing.city,
        deal_score: listing.deal_score,
        estimated_value: listing.estimated_value,
        condition: listing.condition,
      }),
    }),

  getSavedListings: () =>
    apiFetch(`/api/saved-listings?session_id=${getSessionId()}`),

  deleteSavedListing: (source, sourceListingId) =>
    apiFetch(`/api/saved-listings/${encodeURIComponent(source)}/${encodeURIComponent(sourceListingId)}?session_id=${getSessionId()}`, { method: 'DELETE' }),

  addHistory: (query) =>
    apiFetch('/api/search-history', {
      method: 'POST',
      body: JSON.stringify({ query, session_id: getSessionId() }),
    }),

  getHistory: () =>
    apiFetch(`/api/search-history?session_id=${getSessionId()}`),

  previewAlert: (keyword, f = {}) => {
    const qs = new URLSearchParams({ keyword });
    ['min_price', 'max_price', 'category', 'canton', 'sources'].forEach(k => {
      if (f[k] !== undefined && f[k] !== null && f[k] !== '' && f[k] !== 'all') qs.set(k, f[k]);
    });
    return apiFetch(`/api/alerts/preview?${qs.toString()}`);
  },

  createAlert: (keyword, filters = {}) =>
    apiFetch('/api/alerts', {
      method: 'POST',
      body: JSON.stringify({
        keyword,
        session_id: getSessionId(),
        min_price: filters.min_price || null,
        max_price: filters.max_price || null,
        sources: filters.sources?.length ? filters.sources : null,
        category: filters.category && filters.category !== 'all' ? filters.category : null,
        canton: filters.canton && filters.canton !== 'all' ? filters.canton : null,
        only_free: filters.only_free || null,
        email: filters.email || null,
        telegram_chat_id: filters.telegram_chat_id || null,
        telegram_mode: filters.telegram_mode || 'top',
      }),
    }),

  createTelegramLinkToken: () =>
    apiFetch(`/api/telegram/link-token?session_id=${getSessionId()}`, { method: 'POST' }),

  getTelegramStatus: () =>
    apiFetch(`/api/telegram/status?session_id=${getSessionId()}`),

  unlinkTelegram: () =>
    apiFetch(`/api/telegram/unlink?session_id=${getSessionId()}`, { method: 'POST' }),

  getTelegramDealsPrefs: () =>
    apiFetch(`/api/telegram/deals-prefs?session_id=${getSessionId()}`),

  saveTelegramDealsPrefs: (prefs) =>
    apiFetch(`/api/telegram/deals-prefs?session_id=${getSessionId()}`, { method: 'PUT', body: JSON.stringify(prefs) }),

  testTelegramDeals: () =>
    apiFetch(`/api/telegram/deals-test?session_id=${getSessionId()}`, { method: 'POST' }),

  getAlerts: () =>
    apiFetch(`/api/alerts?session_id=${getSessionId()}`),

  deleteAlert: (id) =>
    apiFetch(`/api/alerts/${id}`, { method: 'DELETE' }),

  updateAlert: (id, data) =>
    apiFetch(`/api/alerts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  triggerScrape: () =>
    apiFetch('/api/admin/scrape', { method: 'POST' }),

  getPriceHistory: (source, sourceListingId) =>
    apiFetch(`/api/listings/${encodeURIComponent(source)}/${encodeURIComponent(sourceListingId)}/price-history?session_id=${getSessionId()}`),

  // Una sola richiesta per annuncio: il prefetch al passaggio del mouse e
  // l'apertura della scheda condividono la stessa (prima partivano due fetch).
  getListingImages: (source, sourceListingId) => {
    const key = `${source}:${sourceListingId}`;
    const hit = imagesCache.get(key);
    if (hit && Date.now() - hit.at < 10 * 60 * 1000) return hit.promise;
    const promise = apiFetch(`/api/listings/${encodeURIComponent(source)}/${encodeURIComponent(sourceListingId)}/images`)
      .then((d) => {
        // Solo la miniatura (fetch fallita): non memorizzare, riprova alla prossima apertura
        if (!d?.cached && (d?.images || []).length <= 1) imagesCache.delete(key);
        return d;
      })
      .catch((e) => { imagesCache.delete(key); throw e; });
    imagesCache.set(key, { promise, at: Date.now() });
    return promise;
  },

  translateListing: (source, sourceListingId, lang) =>
    apiFetch(`/api/listings/${encodeURIComponent(source)}/${encodeURIComponent(sourceListingId)}/translate?lang=${lang}`),

  getMarketTrend: (q, days = 30) =>
    apiFetch(`/api/market-trend?q=${encodeURIComponent(q)}&days=${days}`),

  getFeatured: (q = '', category = '', placement = '') => {
    const qs = new URLSearchParams({ limit: '3' });
    if (q) qs.set('q', q);
    if (category && category !== 'all') qs.set('category', category);
    if (placement) qs.set('placement', placement);
    return apiFetch(`/api/featured?${qs.toString()}`);
  },

  getFeaturedOptions: (sessionId) =>
    apiFetch(`/api/featured/options?session_id=${encodeURIComponent(sessionId)}`),

  reportListing: (source, sourceListingId) =>
    apiFetch(`/api/listings/${encodeURIComponent(source)}/${encodeURIComponent(sourceListingId)}/report`, { method: 'POST' }),

  checkAvailability: (source, sourceListingId) =>
    apiFetch(`/api/listings/${encodeURIComponent(source)}/${encodeURIComponent(sourceListingId)}/check`),

  getEbayEnrichment: (itemId) =>
    apiFetch(`/api/ebay/enrich/${encodeURIComponent(itemId)}`),

  updateEmailLanguage: (language) =>
    apiFetch('/api/auth/me/email-language', {
      method: 'PATCH',
      body: JSON.stringify({ language }),
    }),

  getAuctions: (params = {}) => {
    const qs = new URLSearchParams();
    qs.set('page', params.page || 1);
    qs.set('page_size', params.pageSize || 10);
    if (params.minPrice) qs.set('min_price', params.minPrice);
    if (params.maxPrice) qs.set('max_price', params.maxPrice);
    if (params.sources && params.sources.length) qs.set('sources', params.sources.join(','));
    if (params.category && params.category !== 'all') qs.set('category', params.category);
    if (params.canton && params.canton !== 'all') qs.set('canton', params.canton);
    if (params.sort) qs.set('sort', params.sort);
    return apiFetch(`/api/auctions?${qs.toString()}`);
  },

  getProStatus: (sessionId) =>
    apiFetch(`/api/pro/status?session_id=${sessionId}`),

  createProCheckout: (sessionId, email, originUrl, plan = 'pro_monthly') => {
    const qs = new URLSearchParams({ session_id: sessionId, origin_url: originUrl, plan });
    if (email) qs.set('email', email);
    return apiFetch(`/api/checkout/pro?${qs.toString()}`, { method: 'POST' });
  },

  getCheckoutStatus: (stripeSessionId, appSessionId) =>
    apiFetch(`/api/checkout/status/${stripeSessionId}?app_session_id=${appSessionId}`),

  getProAnalytics: () =>
    apiFetch(`/api/pro/analytics?session_id=${getSessionId()}`),

  getProExportCsvUrl: () =>
    `${BACKEND_URL}/api/pro/export-csv?session_id=${getSessionId()}`,

  submitFeedback: (data) =>
    apiFetch('/api/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  submitContact: (data) =>
    apiFetch('/api/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getSimilarListings: (source, sourceListingId, limit = 4) =>
    apiFetch(`/api/listings/${encodeURIComponent(source)}/${encodeURIComponent(sourceListingId)}/similar?limit=${limit}`),

  createPriceWatch: (data) =>
    apiFetch('/api/price-watch', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getAuctionReminders: () => apiFetch('/api/auction-reminders'),

  followAuction: (source, source_listing_id) =>
    apiFetch('/api/auction-reminders', { method: 'POST', body: JSON.stringify({ source, source_listing_id }) }),

  unfollowAuction: (source, sourceListingId) =>
    apiFetch(`/api/auction-reminders/${encodeURIComponent(source)}/${encodeURIComponent(sourceListingId)}`, { method: 'DELETE' }),

  setAuctionReminderChannels: (channels) =>
    apiFetch('/api/auction-reminders/channels', { method: 'PUT', body: JSON.stringify({ channels }) }),

  getPriceWatches: (email) => {
    const qs = new URLSearchParams({ session_id: getSessionId() });
    if (email) qs.set('email', email);
    return apiFetch(`/api/price-watch?${qs.toString()}`);
  },

  updatePriceWatch: (source, sourceListingId, thresholdPrice, email) =>
    apiFetch(`/api/price-watch/${encodeURIComponent(source)}/${encodeURIComponent(sourceListingId)}`, {
      method: 'PATCH',
      body: JSON.stringify({
        session_id: getSessionId(),
        email: email || undefined,
        threshold_price: thresholdPrice,
      }),
    }),

  deletePriceWatch: (source, sourceListingId, email) => {
    const qs = new URLSearchParams({ session_id: getSessionId() });
    if (email) qs.set('email', email);
    return apiFetch(
      `/api/price-watch/${encodeURIComponent(source)}/${encodeURIComponent(sourceListingId)}?${qs.toString()}`,
      { method: 'DELETE' }
    );
  },

  getValuation: (q, canton) => {
    const qs = new URLSearchParams({ q });
    if (canton && canton !== 'all') qs.set('canton', canton);
    return apiFetch(`/api/valuation?${qs.toString()}`);
  },

  getPopularValuations: () =>
    apiFetch('/api/valuation/popular'),
};
